import { TemplatePortal } from '@angular/cdk/portal';
import {
  Component,
  input,
  output,
  signal,
  computed,
  viewChild,
  ElementRef,
  TemplateRef,
  ViewContainerRef,
  HostListener,
  inject,
  DestroyRef,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ErrorsComponent } from '../../errors/errors.component';
import { toSignal, toObservable } from '@angular/core/rxjs-interop';
import { switchMap, startWith } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'app-input-select',
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule, FormsModule, ErrorsComponent],
  templateUrl: './input-select.component.html',
  styleUrl: './input-select.component.css',
})
export class InputSelectComponent {
  trigger = viewChild.required<ElementRef>('trigger');
  dropdownTemplate = viewChild.required<TemplateRef<any>>('dropdownTemplate');
  inputElement = viewChild<ElementRef<HTMLInputElement>>('inputElement');

  id = input<string>('');
  label = input<string>('');
  labelMap = input<Record<string, string>>({});
  controlName = input.required<string>();
  required = input<boolean>(false);
  form = input.required<FormGroup>();
  placeholder = input<string>('');
  options = input<
    {
      value: string | number;
      label: string;
      img?: string | null;
      color?: string;
    }[]
  >([]);
  readonly = input<boolean>(false);
  rowIndex = input<number | undefined>(undefined);
  nextInputIdPattern = input<string | undefined>(undefined);
  totalRows = input<number | undefined>(undefined);
  nextColumnFirstInputIdPattern = input<string | undefined>(undefined);
  showSearch = input<boolean>(true);

  change = output<string | number | null>();

  searchTerm = signal<string>('');
  overlayRef = signal<OverlayRef | null>(null);

  private overlay = inject(Overlay);
  private viewContainerRef = inject(ViewContainerRef);
  private destroyRef = inject(DestroyRef);

  control = computed(() => {
    const form = this.form();
    const controlName = this.controlName();
    if (!form || !controlName) return null as any;
    return form.get(controlName) as FormControl;
  });

  controlValue = toSignal(
    toObservable(this.control).pipe(
      switchMap(ctrl => {
        if (!ctrl) return of(null);
        return ctrl.valueChanges.pipe(startWith(ctrl.value));
      })
    ),
    { initialValue: null }
  );

  selectedLabel = computed(() => {
    const value = this.controlValue();
    const option = this.options().find(opt => opt.value === value);
    const rawLabel = option?.label || '';
    return this.labelMap()[rawLabel] || rawLabel;
  });

  filteredOptions = computed(() => {
    const term = this.searchTerm().toLowerCase();

    if (!term) {
      return this.options();
    }

    return this.options().filter(opt => {
      const rawLabel = opt.label;
      const translated = this.labelMap()[rawLabel] ?? rawLabel;
      return String(translated).toLowerCase().includes(term);
    });
  });

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.closeDropdown();
    });
  }

  toggleDropdown(): void {
    if (this.overlayRef()) {
      this.closeDropdown();
      return;
    }

    this.searchTerm.set('');

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.trigger())
      .withFlexibleDimensions(true)
      .withPush(false)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'start',
          overlayY: 'top',
        },
        {
          originX: 'start',
          originY: 'top',
          overlayX: 'start',
          overlayY: 'bottom',
        },
      ])
      .withDefaultOffsetY(1);

    const newOverlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      width: this.trigger().nativeElement.offsetWidth,
    });

    newOverlayRef.backdropClick().subscribe(() => this.closeDropdown());

    const dropdownPortal = new TemplatePortal(
      this.dropdownTemplate(),
      this.viewContainerRef
    );

    newOverlayRef.attach(dropdownPortal);
    this.overlayRef.set(newOverlayRef);
  }

  selectOption(option: { value: string | number; label: string }): void {
    this.control().setValue(option.value);
    this.control().markAsTouched();
    this.control().markAsDirty();
    this.change.emit(option.value);
    this.closeDropdown();

    setTimeout(() => {
      this.inputElement()?.nativeElement?.focus();
    });
  }

  clearSelection(): void {
    this.control().setValue(null);
    this.change.emit(null);
    this.closeDropdown();
    setTimeout(() => {
      this.inputElement()?.nativeElement?.focus();
    });
  }

  closeDropdown(): void {
    const ref = this.overlayRef();
    if (ref) {
      ref.dispose();
      this.overlayRef.set(null);
    }
  }

  @HostListener('keydown.enter', ['$event'])
  handleEnter(event: KeyboardEvent): void {
    if (this.overlayRef()) return;

    const rowIdx = this.rowIndex();
    if (rowIdx === undefined || rowIdx === null) return;

    event.preventDefault();

    const totalRows = this.totalRows();
    const isLastRow = totalRows !== undefined && rowIdx + 1 >= totalRows;

    const nextColumnPattern = this.nextColumnFirstInputIdPattern();
    if (isLastRow && nextColumnPattern) {
      const nextId = nextColumnPattern.replace('{i}', '0');
      const nextEl = document.getElementById(nextId);
      if (nextEl) {
        (nextEl as HTMLElement).focus();
      }
    } else {
      const nextPattern = this.nextInputIdPattern();
      if (nextPattern) {
        const nextId = nextPattern.replace('{i}', (rowIdx + 1).toString());
        const nextEl = document.getElementById(nextId);
        if (nextEl) {
          (nextEl as HTMLElement).focus();
        }
      }
    }
  }
}
